import React, { useMemo, useState } from "react";
import { Person, ListItem } from "../../types";
import { useEvent } from "../../contexts/EventContext";
import ListItemComponent from "./ListItemComponent";
import { EventSelector } from "../events";
import { GiftIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import "./lists.css";
import "./browse-lists.css";
import DropdownSelect, { DropdownOption } from "../common/DropdownSelect";

interface BrowseListsProps {
  persons: Person[];
  allPersonsLists: Record<number, ListItem[]>;
  currentPersonId?: number;
  eventId?: number; // Optional for backward compatibility
  isLoading?: boolean; // Add loading state
  currentGroupSlug: string; // Group slug for navigation (required)
  onReserve: (listItemId: number) => Promise<void>;
  onCancelReservation: (listItemId: number) => Promise<void>;
}

const BrowseLists: React.FC<BrowseListsProps> = ({
  persons,
  allPersonsLists,
  currentPersonId,
  eventId,
  isLoading = false,
  currentGroupSlug,
  onReserve,
  onCancelReservation,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [priceFilter, setPriceFilter] = useState<
    "all" | "low" | "medium" | "high"
  >("all");
  const [sortBy, setSortBy] = useState<"name" | "price" | "recent">("name");
  const [showFilters, setShowFilters] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  const { events, selectedEvent, selectEvent } = useEvent();

  // Delay spinner to avoid glitch effect on fast loads
  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isLoading) {
      // Show spinner after 500ms delay
      timeoutId = setTimeout(() => {
        setShowSpinner(true);
      }, 500);
    } else {
      // Hide spinner immediately when loading stops
      setShowSpinner(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading]);

  const priceOptions: DropdownOption<"all" | "low" | "medium" | "high">[] =
    useMemo(
      () => [
        { value: "all", label: "Tutti i prezzi" },
        { value: "low", label: "Fino a €50" },
        { value: "medium", label: "€50 - €200" },
        { value: "high", label: "Oltre €200" },
      ],
      []
    );

  const sortOptions: DropdownOption<"name" | "price" | "recent">[] = useMemo(
    () => [
      { value: "name", label: "Ordina per nome" },
      { value: "price", label: "Ordina per prezzo" },
      { value: "recent", label: "Più recenti" },
    ],
    []
  );

  const handleReserve = async (listItemId: number) => {
    await onReserve(listItemId);
  };

  const handleCancelReservation = async (listItemId: number) => {
    await onCancelReservation(listItemId);
  };

  // Calcola statistiche per la singola persona
  const personStats = useMemo(() => {
    if (persons.length !== 1) return null;

    const person = persons[0];
    const personLists = allPersonsLists[person.id] || [];

    let totalItems = personLists.length;
    let availableItems = 0;
    let totalValue = 0;
    let totalReservations = 0;

    for (const item of personLists) {
      if (!item.isReserved) {
        availableItems++;
      }
      if (item.isReserved) {
        totalReservations++;
      }
      if (item.price) {
        totalValue +=
          Number.parseFloat(String(item.price).replace(/[€$,]/g, "")) || 0;
      }
    }

    return {
      totalItems,
      availableItems,
      totalValue,
      totalReservations,
    };
  }, [persons, allPersonsLists]);

  // Filtra e ordina gli elementi
  const getFilteredAndSortedItems = (personLists: ListItem[]) => {
    // Il backend ha già filtrato gli elementi, mostriamo tutti quelli che arrivano
    let filtered = personLists;

    // Filtro per ricerca
    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro per prezzo
    if (priceFilter !== "all") {
      filtered = filtered.filter((item) => {
        if (!item.price) return priceFilter === "low";
        const price =
          Number.parseFloat(String(item.price).replace(/[€$,]/g, "")) || 0;
        switch (priceFilter) {
          case "low":
            return price <= 50;
          case "medium":
            return price > 50 && price <= 200;
          case "high":
            return price > 200;
          default:
            return true;
        }
      });
    }

    // Ordinamento
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "price": {
          const priceA =
            Number.parseFloat(String(a.price || "0").replace(/[€$,]/g, "")) ||
            0;
          const priceB =
            Number.parseFloat(String(b.price || "0").replace(/[€$,]/g, "")) ||
            0;
          return priceA - priceB;
        }
        case "recent": {
          return (
            new Date(b.updatedAt || b.createdAt).getTime() -
            new Date(a.updatedAt || a.createdAt).getTime()
          );
        }
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return filtered;
  };

  // Use eventId prop if provided, otherwise use selected event from context
  const currentEventId = eventId || selectedEvent?.id;

  // Redirect to dashboard if multiple persons (not supported)
  if (persons.length > 1) {
    return (
      <div className="browse-lists">
        <div className="redirect-message">
          <div className="empty-icon">
            <GiftIcon size={48} weight="light" />
          </div>
          <h3>Seleziona una persona specifica</h3>
          <p>
            Per visualizzare le liste regalo, devi selezionare una persona
            specifica dalla dashboard.
          </p>
          <a href={`/${currentGroupSlug}/dashboard`} className="action-button primary">
            Torna alla dashboard
          </a>
        </div>
      </div>
    );
  }

  // Show loading state if data is still loading (with delay)
  if (isLoading && showSpinner) {
    return (
      <div className="browse-lists">
        <div className="loading-state">
          <div className="loading-spinner">⏳</div>
          <p>Caricamento liste regalo...</p>
        </div>
      </div>
    );
  }

  // If loading but spinner not shown yet, show nothing (blank)
  if (isLoading) {
    return <div className="browse-lists"></div>;
  }

  // Show empty state only if not loading and no persons
  if (persons.length === 0) {
    return (
      <div className="browse-lists">
        <div className="empty-state">
          <div className="empty-icon">
            <GiftIcon size={48} weight="light" />
          </div>
          <h3>Nessuna lista disponibile</h3>
          <p>
            Non ci sono altri membri nel gruppo con liste regalo da esplorare.
          </p>
        </div>
      </div>
    );
  }

  // Get the single person and their items
  const person = persons[0];
  const personLists = allPersonsLists[person?.id] || [];
  const filteredItems = getFilteredAndSortedItems(personLists);

  return (
    <div className="browse-lists">
      {/* Event selector - only show if no eventId prop is provided (for backward compatibility) */}
      {!eventId && events.length > 0 && (
        <div className="event-selector-container">
          <EventSelector
            events={events}
            selectedEventId={selectedEvent?.id}
            onEventSelect={selectEvent}
          />
        </div>
      )}

      {/* Show message if no event is selected */}
      {!currentEventId && events.length > 0 && (
        <div className="no-event-selected">
          <p>Seleziona un evento per esplorare le liste regalo.</p>
        </div>
      )}

      {/* Show message if no events exist */}
      {events.length === 0 && (
        <div className="no-events">
          <p>
            Non ci sono eventi disponibili. Contatta un amministratore per
            creare un evento.
          </p>
        </div>
      )}

      {/* Main content - only show if event is selected and person exists */}
      {currentEventId && person && (
        <>
          {/* Se non ci sono regali, mostra solo il messaggio */}
          {personStats?.totalItems === 0 ? (
            <div className="empty-list-message">
              <div className="empty-icon">
                <GiftIcon size={48} weight="light" />
              </div>
              <h3>Lista ancora vuota</h3>
              <p>
                {person.name} non ha ancora aggiunto elementi alla sua lista per
                questo evento.
              </p>
            </div>
          ) : (
            /* Unico box contenente tutto */
            <div className="browse-main-container">
              {/* Statistiche */}
              <div className="browse-stats-header">
                <div className="stat-item">
                  <span className="stat-number">{personStats?.totalItems}</span>
                  <span className="stat-label">Elementi</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">
                    {personStats?.availableItems}
                  </span>
                  <span className="stat-label">Disponibili</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">
                    {personStats?.totalReservations}
                  </span>
                  <span className="stat-label">Prenotati</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">
                    €{personStats?.totalValue.toFixed(0)}
                  </span>
                  <span className="stat-label">Valore</span>
                </div>
              </div>

              {/* Campo di ricerca */}
              <div className="browse-search-section">
                <div className="search-box">
                  <input
                    type="text"
                    placeholder="Cerca per nome o descrizione..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <MagnifyingGlassIcon size={20} className="search-icon" />
                </div>

                <button
                  className={`filter-toggle-btn ${showFilters ? "active" : ""}`}
                  onClick={() => setShowFilters(!showFilters)}
                  title="Mostra/nascondi filtri"
                >
                  ⋮
                </button>
              </div>

              {/* Filtri avanzati */}
              {showFilters && (
                <div className="browse-filter-section">
                  <DropdownSelect
                    options={priceOptions}
                    value={priceFilter}
                    onChange={(nextValue) => setPriceFilter(nextValue)}
                  />
                  <DropdownSelect
                    options={sortOptions}
                    value={sortBy}
                    onChange={(nextValue) => setSortBy(nextValue)}
                  />
                </div>
              )}

              {/* Lista dei regali */}
              <div className="browse-items-section">
                {filteredItems.length === 0 ? (
                  <div className="no-results">
                    {searchTerm || priceFilter !== "all" ? (
                      <p>Nessun elemento corrisponde ai filtri selezionati.</p>
                    ) : (
                      <p>Tutti gli elementi sono già stati riservati.</p>
                    )}
                  </div>
                ) : (
                  <div className="items-grid">
                    {filteredItems.map((item) => (
                      <ListItemComponent
                        key={item.id}
                        item={item}
                        canEdit={false}
                        canReserve={true}
                        isOwnList={currentPersonId === person.id}
                        onReserve={handleReserve}
                        onCancelReservation={handleCancelReservation}
                        isSurpriseEvent={selectedEvent?.isSurprise}
                        isLocked={false} // For browsing lists, we don't need to show locked status
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BrowseLists;
